/**
 * GP-03 order area geometry, free of Phaser so it is unit-tested in node: where each customer
 * stands and where their ticket goes, for one, two or three customers (threeCustomers, rules v2,
 * Rome L1961). All in device px; `u` is the CSS-to-device scale.
 *
 * Wide screens stack the cards in the order column (customer over ticket). Phones put one or two
 * customers beside their tickets. Three on a phone leave each card about a third of a 360 px
 * screen: beside it, the ticket would keep barely two thirds of that width and its item icons
 * would shrink under their count badges (about 17 px for a two-line card at 360 px). So the third
 * card compacts the portraits instead: each customer stands behind the top-left corner of a ticket
 * as wide as the card (head and shoulders above it), and two lines keep icons of 26 px and more.
 * The portraits then own the tickets' top edges, so a goal level's HUD (clearGoal's "Closing time"
 * tag, the items-left or golden-items counter), which straddles the top edge elsewhere, moves to
 * the bottom edge: on a card a third of the screen wide it would cover the face.
 */
import type { Rect } from '../game/layout.js';

export interface CardGeometry {
  /** customer portrait: centre and edge */
  cust: { x: number; y: number; size: number };
  /** ticket: top-left corner and size */
  ticket: Rect;
  /** the ticket edge the goal HUD (tag, counter) straddles: the top, or the bottom when the portrait stands over the top */
  hud: 'top' | 'bottom';
}

/** One card's share of the order area: stacked on wide screens, side by side on phones. */
export function cardPart(order: Rect, n: number, c: number, wide: boolean): Rect {
  return wide ? { x: order.x, y: order.y + (order.h / n) * c, w: order.w, h: order.h / n } : { x: order.x + (order.w / n) * c, y: order.y, w: order.w / n, h: order.h };
}

/** Customer and ticket of one card in its part `r` (n customers on the level). */
export function cardGeometry(r: Rect, n: number, wide: boolean, u: number): CardGeometry {
  if (wide) {
    const size = Math.min(r.w * 0.5, r.h * 0.44);
    const tw = Math.min(r.w * 0.92, 320 * u);
    const ty = r.y + size * 0.96;
    return { cust: { x: r.x + r.w / 2, y: r.y + size / 2, size }, ticket: { x: r.x + (r.w - tw) / 2, y: ty, w: tw, h: Math.min(r.h - size - 4 * u, tw * 0.95) }, hud: 'top' };
  }
  if (n >= 3) {
    // threeCustomers on a phone: the customer stands behind the ticket's top-left corner, head and shoulders above it
    const size = Math.min(r.w * 0.42, r.h * 0.5);
    const tw = r.w - 4 * u;
    const ty = r.y + size * 0.78;
    const th = Math.min(r.y + r.h - ty, tw * 0.95);
    return { cust: { x: r.x + 2 * u + size * 0.55, y: r.y + size / 2, size }, ticket: { x: r.x + 2 * u, y: ty, w: tw, h: th }, hud: 'bottom' };
  }
  const size = Math.min(r.h * 0.94, r.w * (n > 1 ? 0.36 : 0.3));
  const tw = r.w - size * 0.92 - 4 * u;
  const th = Math.min(r.h * 0.92, tw * 0.9);
  return { cust: { x: r.x + size * 0.5, y: r.y + r.h - size * 0.5, size }, ticket: { x: r.x + size * 0.92, y: r.y + (r.h - th) / 2, w: tw, h: th }, hud: 'top' };
}

/**
 * The line grid of one ticket: the columns that give the biggest item icons, and that icon edge.
 * `inner` is the ticket's writing area (below its header).
 */
export function lineGrid(inner: { w: number; h: number }, lines: number): { cols: number; icon: number } {
  let cols = 1;
  let icon = 0;
  for (let c = 1; c <= lines; c++) {
    const size = Math.min((inner.w / c) * 0.56, (inner.h / Math.ceil(lines / c)) * 0.78);
    if (size > icon) {
      icon = size;
      cols = c;
    }
  }
  return { cols, icon };
}

/** One ticket line as drawn: the item icon's edge and centre, and its count badge, relative to the line's cell centre. */
export interface LineCell {
  icon: number;
  ix: number;
  iy: number;
  badge: { x: number; y: number; r: number };
}

/**
 * A line's icon and count badge in its grid cell (`cell`, from lineGrid's columns and rows). A
 * roomy cell keeps the badge on the icon's bottom-right (at least 12 px across, a quarter of a
 * large icon). In a small cell (an icon under 40 px: three cards on a phone, where a two-line card
 * gets icons of about 26 px) that badge would hide nearly half the item, and two items of one
 * shop would be hard to tell apart, so the badge shrinks with the icon (8..12 px radius) and
 * moves out to the icon's corner, overlapping it by a sliver; the icon steps left to make room.
 * It also grows into the room the old badge kept free (up to a fifth larger, so a two-line card's
 * items match a one-line card's at 360 px, about 29 px), as far as the cell holds both.
 */
export function lineCell(icon: number, cell: { w: number; h: number }, u: number): LineCell {
  if (icon >= 40 * u) {
    const iy = -icon * 0.04;
    return { icon, ix: 0, iy, badge: { x: icon * 0.4, y: icon * 0.34, r: Math.max(12 * u, icon * 0.24) } };
  }
  const r = Math.min(12 * u, Math.max(8 * u, icon * 0.3));
  // the icon and its badge, centred together, fit the cell with 1 px to spare on every side
  const size = Math.max(0, Math.min(icon * 1.2, 2 * (cell.w / 2 - 1 * u - r * 0.8), cell.h - 2 * u - r * 0.7));
  const ix = -r * 0.55;
  const iy = -r * 0.35;
  return { icon: size, ix, iy, badge: { x: ix + size / 2 + r * 0.35, y: iy + size / 2 - r * 0.3, r } };
}
